import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';

import apiFetch from '../../auth/apiFetch';
import { properties } from '../../properties';
import { ACTIVITY_TYPES } from './tabs/TabActivities';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

const EVENTS_API = properties.baseURL + properties.adminEventsPath;
const ORDERS_API = properties.baseURL + properties.adminEventOrdersPath;

const STATUS_OPTIONS = [
    { label: 'Pendente', value: 'PENDING' },
    { label: 'Aprovado', value: 'APPROVED' },
    { label: 'Rejeitado', value: 'REJECTED' },
    { label: 'Todos', value: '' },
];

const STATUS_SEVERITY = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger' };
const STATUS_LABEL = { PENDING: 'Pendente', APPROVED: 'Aprovado', REJECTED: 'Rejeitado' };

const activityTypeLabel = (value) =>
    ACTIVITY_TYPES.find(t => t.value === value)?.label ?? value ?? '-';

const ACCREDITATION_TYPES = ['JUDGE_ACCREDITATION', 'INSTRUCTOR_ACCREDITATION', 'EXAMINER_ACCREDITATION'];

// Rótulo do item no pedido: o rótulo do tipo, com sufixo da classe (credenciamento)
// ou do dan (exame). Curso não tem sufixo, e campos nulos caem no rótulo puro.
const orderItemLabel = (item) => {
    const base = activityTypeLabel(item.activityType);
    if (item.activityType === 'GRADE_EXAM' && item.targetDan != null) {
        return `${base} — ${item.targetDan}º dan`;
    }
    if (ACCREDITATION_TYPES.includes(item.activityType) && item.licenseClass != null) {
        return `${base} — classe ${item.licenseClass}`;
    }
    return base;
};

const formatBRL = (value) =>
    `R$ ${Number(value ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Total "cheio" do pedido: soma dos priceSnapshot dos itens.
// Usado quando o pedido é isento, pois nesse caso o total do DTO vem zero.
const fullTotal = (order) =>
    (order.items ?? []).reduce((sum, item) => sum + Number(item.priceSnapshot ?? 0), 0);

function RegistrationList({ eventId }) {
    const [orders, setOrders] = useState([]);
    const [eventName, setEventName] = useState('');
    const [statusFilter, setStatusFilter] = useState('PENDING');
    const [expandedRows, setExpandedRows] = useState(null);

    // receipt: Set de orderIds com fetch em andamento
    const [loadingReceipts, setLoadingReceipts] = useState(new Set());

    // approve
    const [approveDialog, setApproveDialog] = useState(false);
    const [approveTarget, setApproveTarget] = useState(null);

    // reject
    const [rejectDialog, setRejectDialog] = useState(false);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectSubmitted, setRejectSubmitted] = useState(false);

    const toast = useRef(null);

    useEffect(() => { loadEventName(); }, [eventId]);
    useEffect(() => { loadOrders(); }, [eventId, statusFilter]);

    // eventName vem em cada OrderRowDTO, mas a lista pode vir vazia (nenhum
    // pedido no filtro atual) — então mantemos este fetch como fallback do título.
    const loadEventName = async () => {
        try {
            const res = await apiFetch(`${EVENTS_API}/${eventId}`);
            if (res.ok) {
                const data = await res.json();
                setEventName(data.name || '');
            }
        } catch (_) {}
    };

    const loadOrders = async () => {
        try {
            const qs = statusFilter ? `?status=${statusFilter}` : '';
            const res = await apiFetch(`${EVENTS_API}/${eventId}/orders${qs}`);
            if (!res.ok) throw new Error(`Erro ${res.status}`);
            setOrders(await res.json());
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Erro', detail: err.message, life: 4000 });
        }
    };

    // ==================== COMPROVANTE (fetch on click) ====================

    const openReceipt = async (orderId) => {
        setLoadingReceipts(prev => new Set([...prev, orderId]));
        try {
            const res = await apiFetch(`${ORDERS_API}/${orderId}/receipt`);
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Erro ${res.status}`);
            }
            const { url } = await res.json();
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Erro ao abrir comprovante', detail: err.message, life: 4000 });
        } finally {
            setLoadingReceipts(prev => {
                const s = new Set(prev);
                s.delete(orderId);
                return s;
            });
        }
    };

    // ==================== APROVAR ====================

    const confirmApprove = (row) => {
        setApproveTarget(row);
        setApproveDialog(true);
    };

    const executeApprove = async () => {
        try {
            const res = await apiFetch(`${ORDERS_API}/${approveTarget.orderId}/approve`, { method: 'POST' });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Erro ${res.status}`);
            }
            setOrders(prev =>
                prev.map(o => o.orderId === approveTarget.orderId ? { ...o, status: 'APPROVED' } : o)
            );
            toast.current.show({ severity: 'success', summary: 'Aprovado', detail: 'Pedido aprovado', life: 3000 });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: err.message, life: 5000 });
        } finally {
            setApproveDialog(false);
            setApproveTarget(null);
        }
    };

    // ==================== REJEITAR ====================

    const confirmReject = (row) => {
        setRejectTarget(row);
        setRejectReason('');
        setRejectSubmitted(false);
        setRejectDialog(true);
    };

    const executeReject = async () => {
        setRejectSubmitted(true);
        if (!rejectReason.trim()) return;

        try {
            const res = await apiFetch(`${ORDERS_API}/${rejectTarget.orderId}/reject`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason: rejectReason.trim() }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Erro ${res.status}`);
            }
            setOrders(prev =>
                prev.map(o => o.orderId === rejectTarget.orderId ? { ...o, status: 'REJECTED' } : o)
            );
            toast.current.show({ severity: 'success', summary: 'Rejeitado', detail: 'Pedido rejeitado', life: 3000 });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: err.message, life: 5000 });
        } finally {
            setRejectDialog(false);
            setRejectTarget(null);
        }
    };

    // ==================== BODY TEMPLATES ====================

    const statusBodyTemplate = (row) => (
        <Tag
            value={STATUS_LABEL[row.status] ?? row.status}
            severity={STATUS_SEVERITY[row.status] ?? 'info'}
        />
    );

    const totalBodyTemplate = (row) => formatBRL(row.total);

    // Quando isento, o total vem zero mas cada item traz o valor que seria
    // devido — deixamos legível: "Isento — seria R$ 200,00".
    const exemptBodyTemplate = (row) =>
        row.exemptDeclared
            ? <span>Isento — seria {formatBRL(fullTotal(row))}</span>
            : <span>Não</span>;

    // Há comprovante quando o backend devolve o nome do arquivo vigente. A URL é
    // gerada no clique via orderId — não existe (nem é preciso) um fileId aqui.
    const receiptBodyTemplate = (row) =>
        row.receiptFilename ? (
            <Button
                icon="pi pi-file"
                label="Ver"
                className="p-button-sm p-button-outlined"
                tooltip={row.receiptFilename}
                loading={loadingReceipts.has(row.orderId)}
                onClick={() => openReceipt(row.orderId)}
            />
        ) : (
            <span className="text-500">—</span>
        );

    const actionBodyTemplate = (row) => {
        const isPending = row.status === 'PENDING';
        return (
            <>
                <Button
                    icon="pi pi-check"
                    label="Aprovar"
                    className="p-button-sm p-button-success mr-2"
                    disabled={!isPending}
                    onClick={() => confirmApprove(row)}
                />
                <Button
                    icon="pi pi-times"
                    label="Rejeitar"
                    className="p-button-sm p-button-danger"
                    disabled={!isPending}
                    onClick={() => confirmReject(row)}
                />
            </>
        );
    };

    // ==================== EXPANSÃO: ITENS DO PEDIDO ====================

    // Só itens de exame trazem composição congelada (exame/diploma/anuidade).
    // Curso não tem — e nesse caso não mostramos nenhuma linha de composição.
    const hasComposition = (item) =>
        item.examFeeSnapshot != null ||
        item.diplomaFeeSnapshot != null ||
        item.annualFeeSnapshot != null;

    const rowExpansionTemplate = (order) => {
        const items = order.items ?? [];
        if (items.length === 0) {
            return <div className="p-3 text-500">Pedido sem itens</div>;
        }
        return (
            <div className="p-3 flex flex-column gap-3">
                {items.map((item) => (
                    <div key={item.activityId} className="p-3 border-round surface-100">
                        {/* Cabeçalho do item: rótulo do tipo com o sufixo de classe
                            (credenciamento) ou dan (exame), e o valor do item. */}
                        <div className="flex justify-content-between align-items-center">
                            <span className="font-semibold text-800">
                                {orderItemLabel(item)}
                            </span>
                            <span className="font-semibold">{formatBRL(item.priceSnapshot)}</span>
                        </div>

                        {hasComposition(item) && (
                            <div className="mt-2 pl-3 flex flex-column gap-2 border-left-2 surface-border">
                                {item.examFeeSnapshot != null && (
                                    <div className="flex justify-content-between align-items-center">
                                        <span>Taxa de exame</span>
                                        <span>{formatBRL(item.examFeeSnapshot)}</span>
                                    </div>
                                )}

                                <div className="flex justify-content-between align-items-center">
                                    <span>Taxa de diploma</span>
                                    {item.diplomaFeeSnapshot != null ? (
                                        <span>{formatBRL(item.diplomaFeeSnapshot)}</span>
                                    ) : (
                                        <span className="text-500 font-italic">não solicitado</span>
                                    )}
                                </div>

                                {/* Anuidade sempre listada nos itens de exame: com valor,
                                    ou "já paga" quando o snapshot é nulo (parcela não cobrada). */}
                                <div className="flex justify-content-between align-items-center">
                                    <span>Anuidade JKA</span>
                                    {item.annualFeeSnapshot != null ? (
                                        <span>{formatBRL(item.annualFeeSnapshot)}</span>
                                    ) : (
                                        <span className="text-500 font-italic">anuidade do ano já paga</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    // ==================== RENDER ====================

    return (
        <div className="datatable-crud-demo surface-card p-4 border-round shadow-2">
            <Toast ref={toast} />

            <div className="flex align-items-center mb-4 gap-3">
                <Button
                    icon="pi pi-arrow-left"
                    className="p-button-text"
                    onClick={() => { window.location.href = '/events'; }}
                    tooltip="Voltar para Eventos"
                />
                <div className="text-3xl text-800 font-bold">
                    Inscrições{eventName ? ` — ${eventName}` : ''}
                </div>
            </div>

            <div className="flex align-items-center mb-3 gap-2">
                <label className="font-semibold mr-1">Status:</label>
                <Dropdown
                    value={statusFilter}
                    options={STATUS_OPTIONS}
                    onChange={(e) => setStatusFilter(e.value)}
                    style={{ minWidth: '150px' }}
                />
            </div>

            <DataTable
                value={orders}
                dataKey="orderId"
                expandedRows={expandedRows}
                onRowToggle={(e) => setExpandedRows(e.data)}
                rowExpansionTemplate={rowExpansionTemplate}
                paginator rows={20} rowsPerPageOptions={[10, 20, 50]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} pedidos"
                emptyMessage="Nenhum pedido encontrado"
                responsiveLayout="scroll"
            >
                <Column expander style={{ width: '3rem' }} />
                <Column field="jkaId" header="FL" sortable style={{ minWidth: '7rem' }} />
                <Column field="affiliateName" header="Nome" sortable style={{ minWidth: '16rem' }} />
                <Column header="Total" body={totalBodyTemplate} style={{ minWidth: '8rem' }} />
                <Column header="Isento" body={exemptBodyTemplate} style={{ minWidth: '12rem' }} />
                <Column header="Status" body={statusBodyTemplate} style={{ minWidth: '8rem' }} />
                <Column header="Comprovante" body={receiptBodyTemplate} style={{ minWidth: '9rem' }} />
                <Column header="Ações" body={actionBodyTemplate} style={{ minWidth: '15rem' }} />
            </DataTable>

            {/* Dialog: Aprovar */}
            <Dialog
                visible={approveDialog}
                style={{ width: '420px' }}
                header="Confirmar Aprovação"
                modal
                footer={
                    <>
                        <Button label="Cancelar" icon="pi pi-times" className="p-button-text"
                            onClick={() => setApproveDialog(false)} />
                        <Button label="Aprovar" icon="pi pi-check" className="p-button-success"
                            onClick={executeApprove} />
                    </>
                }
                onHide={() => setApproveDialog(false)}
            >
                <div className="flex align-items-center gap-3">
                    <i className="pi pi-check-circle" style={{ fontSize: '2rem', color: 'var(--green-500)' }} />
                    {approveTarget && (
                        <span>Aprovar o pedido de <b>{approveTarget.affiliateName}</b> ({formatBRL(approveTarget.total)})?</span>
                    )}
                </div>
            </Dialog>

            {/* Dialog: Rejeitar */}
            <Dialog
                visible={rejectDialog}
                style={{ width: '480px' }}
                header="Rejeitar Pedido"
                modal
                className="p-fluid"
                footer={
                    <>
                        <Button label="Cancelar" icon="pi pi-times" className="p-button-text"
                            onClick={() => setRejectDialog(false)} />
                        <Button label="Rejeitar" icon="pi pi-times" className="p-button-danger"
                            onClick={executeReject} />
                    </>
                }
                onHide={() => setRejectDialog(false)}
            >
                <div className="field">
                    <label htmlFor="rejectReason" className="font-semibold">
                        Motivo da rejeição *
                    </label>
                    <InputTextarea
                        id="rejectReason"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={4}
                        autoResize
                        className={rejectSubmitted && !rejectReason.trim() ? 'p-invalid' : ''}
                    />
                    {rejectSubmitted && !rejectReason.trim() && (
                        <small className="p-error">Motivo é obrigatório</small>
                    )}
                </div>
            </Dialog>
        </div>
    );
}

export default RegistrationList;
